import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { socket } from '#AdminPanelFrontend/services/socket';
import { CgRadioCheck } from 'react-icons/cg';

interface SidebarItem {
	title: string;
	icon: React.ElementType;
	path?: string;
	componentPath?: string;
	subItems?: SidebarItem[];
	params?: string[];
}

interface MenuContextType {
	menuItems: SidebarItem[];
	loading: boolean;
	setMenuVisible: (visible: boolean) => void;
}

const MenuContext = createContext<MenuContextType>({ menuItems: [], loading: true, setMenuVisible: () => {} });

export const MenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [menuItems, setMenuItems] = useState<SidebarItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [menuVisible, setMenuVisible] = useState(true);
	const hasFetchedMenu = useRef(false);

	useEffect(() => {
		if (!hasFetchedMenu.current) {
			socket.timeout(5000).emit('Config:ListMenu', {}, () => {});
			hasFetchedMenu.current = true;
		}

		socket.on('Config:ListMenu', (data) => {
			if (data.success) {
				setMenuItems(getSidebarItems(data.data.nav));
				setLoading(false);
			}
		});

		return () => {
			socket.off('Config:ListMenu');
		};
	}, []);

	return <MenuContext.Provider value={{ menuItems, loading, setMenuVisible }}>{children}</MenuContext.Provider>;
};

export const useMenu = () => useContext(MenuContext);

const getSidebarItems = (response: object): SidebarItem[] => {
	if (Object.keys(response).length < 1) {
		return _getDefaultSidebarItems();
	}
	const list: SidebarItem[] = [
		{
			title: 'Dashboard',
			icon: () => (
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					className="w-4 h-4"
				>
					<rect width="7" height="9" x="3" y="3" rx="1" />
					<rect width="7" height="5" x="14" y="3" rx="1" />
					<rect width="7" height="9" x="14" y="12" rx="1" />
					<rect width="7" height="5" x="3" y="16" rx="1" />
				</svg>
			),
			path: '/',
			componentPath: 'Dashboard',
		},
	];
	for (const [key, value] of Object.entries(response)) {
		const subList: SidebarItem[] = [];
		if (value.subItems && value.subItems.length > 0) {
			for (const item of value.subItems) {
				const path = `/${key.toLowerCase()}/${item.name.toLowerCase()}`;
				subList.push({
					title: item.name,
					icon: () => <span className="w-2 h-2 rounded-full bg-current" />,
					path: path,
					componentPath: `${key}/${item.name}`,
					params: item.params || [],
				});
			}
		}

		const entry: SidebarItem = {
			title: key,
			icon: () => <CgRadioCheck />,
			componentPath: `${key}`,
		};

		if (subList.length > 0) {
			entry.subItems = subList;
		} else {
			entry.path = `/${key.toLowerCase()}`;
		}
		list.push(entry);
	}

	list.push({
		title: 'Settings',
		icon: () => (
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
				className="w-4 h-4"
			>
				<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
				<circle cx="12" cy="12" r="3" />
			</svg>
		),
		path: '/settings',
		componentPath: 'Settings',
	});
	return list;
};

const _getDefaultSidebarItems = (): SidebarItem[] => {
	return [
		{
			title: 'Dashboard',
			icon: () => (
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					className="w-4 h-4"
				>
					<rect width="7" height="9" x="3" y="3" rx="1" />
					<rect width="7" height="5" x="14" y="3" rx="1" />
					<rect width="7" height="9" x="14" y="12" rx="1" />
					<rect width="7" height="5" x="3" y="16" rx="1" />
				</svg>
			),
			path: '/',
		},
		{
			title: 'Settings',
			icon: () => (
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					className="w-4 h-4"
				>
					<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
					<circle cx="12" cy="12" r="3" />
				</svg>
			),
			path: '/settings',
		},
	];
};
